import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "submitBtn", "errors"]
  static values = { updateUrl: String, paramKey: String }

  connect() {
    // Track whether the user explicitly cleared a bidding contractor via the
    // team-card "Remove company" confirmation modal. Sent with the save payload so
    // the backend can distinguish a deliberate clear-to-empty from an accidental
    // empty submission (which it ignores to avoid wiping the list). Mirrors the
    // cleared_roles signal in edit_dev_team_controller.
    this._cleared = false
    this._onExplicitRemove = () => { this._cleared = true }
    this.element.addEventListener("team-card:explicit-remove", this._onExplicitRemove)
  }

  disconnect() {
    if (this._onExplicitRemove) {
      this.element.removeEventListener("team-card:explicit-remove", this._onExplicitRemove)
    }
  }

  addCard(event) {
    const container = this.containerTarget
    const firstCard = container.querySelector("[data-controller*='team-card']")
    if (firstCard) {
      const clone = firstCard.cloneNode(true)
      clone.querySelectorAll("input").forEach(input => { input.value = "" })
      clone.querySelectorAll("[data-team-card-target='memberChips']").forEach(el => { el.innerHTML = "" })
      delete clone.dataset.teamCardInitialCompanyIdValue
      delete clone.dataset.teamCardInitialCompanyNameValue
      delete clone.dataset.teamCardInitialMembersValue
      const addBtn = clone.querySelector("[data-team-card-target='addMembersBtn']")
      if (addBtn) { addBtn.classList.add("hidden"); addBtn.classList.remove("inline-flex") }
      container.appendChild(clone)
    }
  }

  async save() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this._hideErrors()
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Saving..."

    try {
      const biddingContractors = this._collectCards()

      const response = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ [this.paramKeyValue || "bidding_contractors"]: biddingContractors, cleared: this._cleared })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        window.location.reload()
      } else {
        this._showErrors(data.errors || ["Update failed"])
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Save Changes"
      }
    } catch {
      this._showErrors(["A network error occurred. Please try again."])
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = "Save Changes"
    }
  }

  _showErrors(messages) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.innerHTML = messages.map(m => `<p>${m}</p>`).join("")
    this.errorsTarget.classList.remove("hidden")
  }

  _hideErrors() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.innerHTML = ""
  }

  _collectCards() {
    const cards = this.containerTarget.querySelectorAll("[data-controller*='team-card']")
    const result = []

    cards.forEach(card => {
      const companyHidden = card.querySelector("[data-team-card-target='companyHidden']")
      const companyId = companyHidden?.value
      if (!companyId) return

      const chips = card.querySelectorAll("[data-team-card-target='memberChips'] [data-chip-email]")
      const members = Array.from(chips).map(chip => chip.dataset.chipEmail).filter(Boolean)

      result.push({ company_id: companyId, members })
    })

    return result
  }
}
